import React, { useState, useEffect, useMemo } from 'react';
import { getNearDuplicates, getSuggestedNewFamilies, getSuggestedAddFamilies } from '../utils/gameFamilyIntelligence';
import axios from 'axios';
import { useGamesCache } from '../contexts/GamesContext';
import {
  Tabs,
  Tab,
  Box,
  Card,
  CardContent,
  Typography,
  Slider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  TextField,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Autocomplete
} from '@mui/material';
import {
  CompareArrows as MergeIcon,
  Block as IgnoreIcon,
  Refresh as RefreshIcon,
  DoneAll as DoneIcon,
  Settings as SettingsIcon,
  Layers as FamilyIcon,
  Folder as FolderIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FolderOpen as FolderOpenIcon,
  Close as CloseIcon,
  HelpOutline as HelpIcon
} from '@mui/icons-material';

const SightingCropImage = ({ WebsitesDataID, BoundingBoxID, BoundingBox }) => {
  const [cropSrc, setCropSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    if (!WebsitesDataID || !BoundingBox) {
      setError(true);
      setLoading(false);
      return;
    }

    const loadCrop = async () => {
      try {
        // 1. Fetch websites data details to get ImageLocation
        const websitesRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/screenshots/${WebsitesDataID}`,
          {
            headers: { 'x-api-key': process.env.REACT_APP_API_KEY }
          }
        );
        const imageLocation = websitesRes.data.ImageLocation;
        if (!imageLocation) throw new Error('No ImageLocation found');

        // 2. Call backend crop endpoint to crop it natively
        const cropRes = await axios.post(
          `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/game-management/upload-crop`,
          {
            image_location: imageLocation,
            bounding_box: BoundingBox
          },
          {
            headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY }
          }
        );

        if (active) {
          setCropSrc(cropRes.data.url);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load crop:', err);
        if (active) {
          setError(true);
          setLoading(false);
        }
      }
    };

    loadCrop();
    return () => {
      active = false;
    };
  }, [WebsitesDataID, BoundingBoxID, BoundingBox]);

  if (error) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'action.disabledBackground' }}>
        <Typography variant="caption" color="text.secondary">No Image</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <img 
      src={cropSrc} 
      alt="Crop Sighting" 
      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
    />
  );
};

const GameManagement = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [similarityThreshold, setSimilarityThreshold] = useState(0.92);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Expander and Game details cache states
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [gameDetails, setGameDetails] = useState({});
  const [detailsLoading, setDetailsLoading] = useState({});
  
  // Notification states
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  
  // Dialog confirmation states
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: '', // 'merge' | 'ignore'
    data: null
  });

  // Game Families States
  const [families, setFamilies] = useState([]);
  const [familiesLoading, setFamiliesLoading] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [familyForm, setFamilyForm] = useState({
    DisplayName: '',
    SeriesName: '',
    Slug: '',
    StudioID: '',
    GameIDs: []
  });
  const [treeSearch, setTreeSearch] = useState('');
  const [expandedStudios, setExpandedStudios] = useState(new Set());
  const [gameSearchQuery, setGameSearchQuery] = useState('');
  const [gameSearchResults, setGameSearchResults] = useState([]);
  const [searchingGames, setSearchingGames] = useState(false);

  // Near Duplicate Families State
  const [nearDuplicatesDialogOpen, setNearDuplicatesDialogOpen] = useState(false);
  const [activeMatchIdx, setActiveMatchIdx] = useState(0);
  const [selectedTargetFamilyId, setSelectedTargetFamilyId] = useState('');

  // Suggestion Dialogs and All Games Cache
  const { games: allGames, gameMap, fetchGames: fetchGamesCached, setGames: setAllGames, studios, fetchStudios: fetchStudiosCached } = useGamesCache();
  const [suggestNewFamiliesDialogOpen, setSuggestNewFamiliesDialogOpen] = useState(false);
  const [suggestAddFamiliesDialogOpen, setSuggestAddFamiliesDialogOpen] = useState(false);
  const [ignoredSeries, setIgnoredSeries] = useState([]);
  const [customNames, setCustomNames] = useState({});
const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [whatsNewLang, setWhatsNewLang] = useState('en');
  const [manualSourceGame, setManualSourceGame] = useState(null);
  const [manualTargetGame, setManualTargetGame] = useState(null);

  const nearDuplicates = useMemo(() => {
    return getNearDuplicates(families);
  }, [families]);

  const suggestedNewFamilies = useMemo(() => {
    return getSuggestedNewFamilies(allGames, families, studios, ignoredSeries);
  }, [allGames, families, studios, ignoredSeries]);

  const suggestedAddFamilies = useMemo(() => {
    return getSuggestedAddFamilies(families, allGames, studios);
  }, [families, allGames, studios]);

  useEffect(() => {
    const seen = localStorage.getItem('whats_new_seen_game_mgmt_v1');
    if (!seen) {
      setWhatsNewOpen(true);
    }
  }, []);

  const handleCloseWhatsNew = () => {
    setWhatsNewOpen(false);
  };

  const handleDontShowWhatsNew = () => {
    localStorage.setItem('whats_new_seen_game_mgmt_v1', 'true');
    setWhatsNewOpen(false);
  };

  const getGameName = (id) => {
    const game = allGames.find(g => g.GameID === id);
    return game ? game.Name : id;
  };

  const handleOpenNearDuplicates = () => {
    setActiveMatchIdx(0);
    const matches = nearDuplicates;
    if (matches.length > 0) {
      setSelectedTargetFamilyId(matches[0].familyA.FamilyID);
    } else {
      setSelectedTargetFamilyId('');
    }
    setNearDuplicatesDialogOpen(true);
  };

  const handleNextMatch = (matches) => {
    const nextIdx = activeMatchIdx + 1;
    setActiveMatchIdx(nextIdx);
    setSelectedTargetFamilyId(matches[nextIdx].familyA.FamilyID);
  };

  const handlePrevMatch = (matches) => {
    const prevIdx = activeMatchIdx - 1;
    setActiveMatchIdx(prevIdx);
    setSelectedTargetFamilyId(matches[prevIdx].familyA.FamilyID);
  };

  const handleAddGamesToFamily = async (family, newGames) => {
    setActionLoading(true);
    try {
      const newGameIDs = newGames.map(g => g.GameID);
      const combinedIDs = Array.from(new Set([
        ...(family.GameIDs || []),
        ...newGameIDs
      ])).sort();

      const response = await axios.put(`${API_URL}/game-management/families/${family.FamilyID}`, {
        ...family,
        GameIDs: combinedIDs
      }, {
        headers: { 'x-api-key': API_KEY }
      });

      showNotification(`Successfully added ${newGames.length} game(s) to "${family.DisplayName}"!`, 'success');

      // Update local state
      setFamilies(prev => prev.map(f => f.FamilyID === family.FamilyID ? response.data.family : f));
      if (selectedFamily?.FamilyID === family.FamilyID) {
        setSelectedFamily(response.data.family);
      }

    } catch (err) {
      console.error(err);
      showNotification('Failed to add games to family.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAcceptSuggestedNewFamily = async (suggested, customName) => {
    setActionLoading(true);
    try {
      const name = customName && customName.trim() ? customName.trim() : null;
      const newFamily = {
        DisplayName: name 
          ? name 
          : `${suggested.SeriesName.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} Series`,
        SeriesName: name 
          ? name.toLowerCase() 
          : suggested.SeriesName,
        Slug: name 
          ? name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') 
          : suggested.SeriesName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
        StudioID: suggested.StudioID,
        GameIDs: suggested.GameIDs
      };

      const res = await axios.post(`${API_URL}/game-management/families`, newFamily, {
        headers: { 'x-api-key': API_KEY }
      });
      
      showNotification(`Created new family "${newFamily.DisplayName}" successfully!`, 'success');
      setFamilies(prev => [...prev, res.data.family]);
    } catch (err) {
      console.error(err);
      showNotification(err.response?.data?.error || err.response?.data || 'Failed to create family.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteFamilyFromDuplicates = async (family) => {
    if (!window.confirm(`Are you sure you want to permanently delete the family "${family.DisplayName}"?`)) return;
    
    setActionLoading(true);
    try {
      await axios.delete(`${API_URL}/game-management/families/${family.FamilyID}`, {
        headers: { 'x-api-key': API_KEY }
      });
      showNotification(`Deleted family "${family.DisplayName}" successfully!`, 'success');
      
      setFamilies(prev => prev.filter(f => f.FamilyID !== family.FamilyID));
      
      if (selectedFamily?.FamilyID === family.FamilyID) {
        setSelectedFamily(null);
      }
      
      const currentMatches = nearDuplicates;
      const nextMatches = currentMatches.filter(match => 
        match.familyA.FamilyID !== family.FamilyID && match.familyB.FamilyID !== family.FamilyID
      );
      
      if (nextMatches.length === 0) {
        setNearDuplicatesDialogOpen(false);
      } else {
        const nextIdx = Math.min(activeMatchIdx, nextMatches.length - 1);
        setActiveMatchIdx(nextIdx);
        setSelectedTargetFamilyId(nextMatches[nextIdx].familyA.FamilyID);
      }
    } catch (err) {
      console.error(err);
      showNotification('Failed to delete family.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const API_URL = process.env.REACT_APP_API_GAMES_STUDIOS_URL;
  const API_KEY = process.env.REACT_APP_API_GAMES_STUDIOS_KEY;

  const showNotification = (message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/game-management/intelligence`, {
        params: {
          type: 'merge',
          minSimilarity: similarityThreshold
        },
        headers: {
          'x-api-key': API_KEY
        }
      });
      setSuggestions(response.data.suggestions || []);
      showNotification(`Loaded ${response.data.suggestions?.length || 0} suggestion pairs.`, 'success');
    } catch (err) {
      console.error(err);
      showNotification(err.response?.data?.error || 'Failed to fetch suggestions.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchGameDetails = async (gameId) => {
    if (!gameId || gameDetails[gameId] || detailsLoading[gameId]) return;
    
    setDetailsLoading(prev => ({ ...prev, [gameId]: true }));
    try {
      const response = await axios.get(`${API_URL}/games/${gameId}/summary`, {
        headers: {
          'x-api-key': API_KEY
        }
      });
      setGameDetails(prev => ({ ...prev, [gameId]: response.data }));
    } catch (err) {
      console.error(`Failed to fetch details for game ${gameId}:`, err);
    } finally {
      setDetailsLoading(prev => ({ ...prev, [gameId]: false }));
    }
  };

  const toggleRowExpand = (rowKey, gameIdA, gameIdB) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowKey)) {
      newExpanded.delete(rowKey);
    } else {
      newExpanded.add(rowKey);
      fetchGameDetails(gameIdA);
      fetchGameDetails(gameIdB);
    }
    setExpandedRows(newExpanded);
  };


  const fetchFamiliesData = async () => {
    setFamiliesLoading(true);
    try {
      const familiesRes = await axios.get(`${API_URL}/game-management/families`, {
        headers: { 'x-api-key': API_KEY }
      });
      setFamilies(familiesRes.data.families || []);

      await fetchStudiosCached();
      await fetchGamesCached();

      // Fetch ignored series
      const intelligenceRes = await axios.get(`${API_URL}/game-management/intelligence`, {
        params: { type: 'family' },
        headers: { 'x-api-key': API_KEY }
      });
      setIgnoredSeries(intelligenceRes.data.ignored_series || []);

      // Start with all studios collapsed on load
      setExpandedStudios(new Set());

    } catch (err) {
      console.error(err);
      showNotification('Failed to fetch game families or lookup data.', 'error');
    } finally {
      setFamiliesLoading(false);
    }
  };

  useEffect(() => {
    fetchSuggestions();
    fetchStudiosCached();
  }, []);

  useEffect(() => {
    if (activeTab === 1) {
      fetchFamiliesData();
    } else if (activeTab === 2) {
      fetchGamesCached();
    }
  }, [activeTab]);

  const handleIgnoreSeriesAction = async (phrase) => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/game-management/execute`, {
        action: 'ignore_series',
        phrase
      }, {
        headers: {
          'x-api-key': API_KEY
        }
      });
      
      const phraseClean = phrase.toLowerCase().trim();
      setIgnoredSeries(prev => [...prev, phraseClean]);
      showNotification(`Successfully ignored series phrase "${phrase}"!`, 'success');
    } catch (err) {
      console.error(err);
      showNotification(err.response?.data?.error || 'Failed to ignore series phrase.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMergeAction = async (sourceId, targetId) => {
    setActionLoading(true);
    try {
      const response = await axios.post(`${API_URL}/game-management/execute`, {
        action: 'merge',
        sourceId,
        targetId
      }, {
        headers: {
          'x-api-key': API_KEY
        }
      });
      
      showNotification(response.data.message || 'Successfully merged games!', 'success');
      
      // Update suggestions list
      setSuggestions(prev => prev.filter(item => 
        !(item.gameA.GameID === sourceId && item.gameB.GameID === targetId) &&
        !(item.gameA.GameID === targetId && item.gameB.GameID === sourceId)
      ));

      // 1. Surgically sync local allGames array
      let sourceTimestamp = null;
      setAllGames(prevGames => {
        const sourceGame = prevGames.find(g => g.GameID === sourceId);
        if (sourceGame) {
          sourceTimestamp = sourceGame.Timestamp;
        }
        
        // Remove source game, and update target game's timestamp if source was older
        const updatedGames = prevGames
          .filter(g => g.GameID !== sourceId)
          .map(g => {
            if (g.GameID === targetId && sourceTimestamp && (!g.Timestamp || sourceTimestamp < g.Timestamp)) {
              return { ...g, Timestamp: sourceTimestamp };
            }
            return g;
          });

        // Keep local cache perfectly updated
        try {
          localStorage.setItem('tm_games_cache', JSON.stringify(updatedGames));
          localStorage.setItem('tm_games_cache_timestamp', Date.now().toString());
        } catch (storageError) {
          console.warn('Failed to write games cache to localStorage after merge:', storageError);
        }

        return updatedGames;
      });

      // 2. Surgically sync local Game Families references
      setFamilies(prevFamilies => {
        return prevFamilies.map(fam => {
          const ids = fam.GameIDs || [];
          if (ids.includes(sourceId)) {
            // Replace sourceId with targetId
            const mappedIDs = ids.map(id => id === sourceId ? targetId : id);
            // Deduplicate and sort
            const cleanIDs = Array.from(new Set(mappedIDs)).sort();
            return { ...fam, GameIDs: cleanIDs };
          }
          return fam;
        });
      });


    } catch (err) {
      console.error(err);
      showNotification(err.response?.data?.error || 'Failed to execute merge.', 'error');
    } finally {
      setActionLoading(false);
      setConfirmDialog({ open: false, type: '', data: null });
    }
  };

  const handleIgnoreAction = async (gameIdA, gameIdB) => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/game-management/execute`, {
        action: 'ignore_merge',
        gameIdA,
        gameIdB
      }, {
        headers: {
          'x-api-key': API_KEY
        }
      });
      
      showNotification('Successfully added pair to ignore list.', 'success');
      setSuggestions(prev => prev.filter(item => 
        !(item.gameA.GameID === gameIdA && item.gameB.GameID === gameIdB) &&
        !(item.gameA.GameID === gameIdB && item.gameB.GameID === gameIdA)
      ));
    } catch (err) {
      console.error(err);
      showNotification(err.response?.data?.error || 'Failed to ignore pair.', 'error');
    } finally {
      setActionLoading(false);
      setConfirmDialog({ open: false, type: '', data: null });
    }
  };

  const openConfirmation = (type, data) => {
    setConfirmDialog({
      open: true,
      type,
      data
    });
  };

  const renderSimilarityChip = (val) => {
    let color = 'error';
    if (val >= 0.95) color = 'success';
    else if (val >= 0.90) color = 'warning';
    
    return (
      <Chip 
        label={`${(val * 100).toFixed(1)}%`} 
        color={color} 
        size="small" 
        sx={{ fontWeight: 'bold', minWidth: '60px' }}
      />
    );
  };

  // Game Families helpers
  const toggleStudioExpand = (studioId) => {
    setExpandedStudios(prev => {
      const next = new Set(prev);
      if (next.has(studioId)) {
        next.delete(studioId);
      } else {
        next.add(studioId);
      }
      return next;
    });
  };

  const searchGames = async () => {
    if (!gameSearchQuery.trim()) return;
    setSearchingGames(true);
    try {
      const res = await axios.get(`${API_URL}/games`, {
        params: { search: gameSearchQuery, limit: 10 },
        headers: { 'x-api-key': API_KEY }
      });
      setGameSearchResults(res.data.games || []);
      
    } catch (err) {
      console.error(err);
      showNotification('Failed to search games.', 'error');
    } finally {
      setSearchingGames(false);
    }
  };

  const handleSelectFamily = (family) => {
    setSelectedFamily(family);
    setIsCreatingNew(false);
    setFamilyForm({
      DisplayName: family.DisplayName || '',
      SeriesName: family.SeriesName || '',
      Slug: family.Slug || '',
      StudioID: family.StudioID || 'unknown',
      GameIDs: family.GameIDs || []
    });
    setGameSearchQuery('');
    setGameSearchResults([]);
  };

  const handleStartCreateNew = () => {
    setSelectedFamily(null);
    setIsCreatingNew(true);
    setFamilyForm({
      DisplayName: '',
      SeriesName: '',
      Slug: '',
      StudioID: studios[0]?.StudioID || 'unknown',
      GameIDs: []
    });
    setGameSearchQuery('');
    setGameSearchResults([]);
  };

  const handleRemoveGameFromFamily = (gameId) => {
    setFamilyForm(prev => ({
      ...prev,
      GameIDs: prev.GameIDs.filter(id => id !== gameId)
    }));
  };

  const handleAddGameToFamily = (game) => {
    if (familyForm.GameIDs.includes(game.GameID)) {
      showNotification('Game is already in this family.', 'warning');
      return;
    }
    setFamilyForm(prev => ({
      ...prev,
      GameIDs: [...prev.GameIDs, game.GameID]
    }));
    showNotification(`Added ${game.Name} to family.`, 'success');
  };

  const handleSaveFamily = async () => {
    if (!familyForm.DisplayName.trim()) {
      showNotification('Display Name is required.', 'error');
      return;
    }
    setActionLoading(true);
    try {
      if (isCreatingNew) {
        const res = await axios.post(`${API_URL}/game-management/families`, familyForm, {
          headers: { 'x-api-key': API_KEY }
        });
        showNotification('Created family successfully!', 'success');
        setFamilies(prev => [...prev, res.data.family]);
        handleSelectFamily(res.data.family);
      } else {
        const res = await axios.put(`${API_URL}/game-management/families/${selectedFamily.FamilyID}`, familyForm, {
          headers: { 'x-api-key': API_KEY }
        });
        showNotification('Updated family successfully!', 'success');
        setFamilies(prev => prev.map(f => f.FamilyID === selectedFamily.FamilyID ? res.data.family : f));
        setSelectedFamily(res.data.family);
      }
    } catch (err) {
      console.error(err);
      showNotification(err.response?.data || 'Failed to save family.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteFamily = async () => {
    if (!selectedFamily) return;
    if (!window.confirm(`Are you sure you want to delete the family "${selectedFamily.DisplayName}"?`)) return;
    
    setActionLoading(true);
    try {
      await axios.delete(`${API_URL}/game-management/families/${selectedFamily.FamilyID}`, {
        headers: { 'x-api-key': API_KEY }
      });
      showNotification('Deleted family successfully!', 'success');
      setFamilies(prev => prev.filter(f => f.FamilyID !== selectedFamily.FamilyID));
      setSelectedFamily(null);
    } catch (err) {
      console.error(err);
      showNotification('Failed to delete family.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Grouping families for Tree rendering
  const getFilteredTreeData = () => {
    const searchLower = treeSearch.toLowerCase();
    
    // Group all active families by StudioID
    const grouped = {};
    families.forEach(fam => {
      const studioId = fam.StudioID || 'unknown';
      const studio = studios.find(s => s.StudioID === studioId);
      const studioName = studio ? studio.Name : 'unknown';
      
      const matchesSearch = 
        fam.DisplayName.toLowerCase().includes(searchLower) ||
        fam.SeriesName.toLowerCase().includes(searchLower) ||
        studioName.toLowerCase().includes(searchLower);

      if (!searchLower || matchesSearch) {
        grouped[studioId] = grouped[studioId] || {
          studioId,
          studioName: studio ? studio.Name.toUpperCase() : 'UNKNOWN STUDIO',
          families: []
        };
        grouped[studioId].families.push(fam);
      }
    });

    return Object.values(grouped).sort((a, b) => a.studioName.localeCompare(b.studioName));
  };

  const renderGameDetailCompare = (game, details) => {
    if (!details) {
      return (
        <Box sx={{ p: 1 }}>
          <Typography variant="body2" color="text.secondary">No details fetched.</Typography>
        </Box>
      );
    }

    const { TotalOccurrences, LastSeenTimestamp, RecentTiles } = details;
    const formattedLastSeen = LastSeenTimestamp 
      ? new Date(LastSeenTimestamp).toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      : 'Never Sighted';

    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
              Occurrences in DB
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '1rem' }}>
              {TotalOccurrences ?? 0} times
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
              Most Recent Sighting
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {formattedLastSeen}
            </Typography>
          </Grid>
        </Grid>
        
        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 'bold', mb: 1, textTransform: 'uppercase' }}>
          Recent Game Crops
        </Typography>
        {RecentTiles && RecentTiles.length > 0 ? (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {RecentTiles.map((tile, idx) => {
              const dateStr = tile.Timestamp ? tile.Timestamp.split('T')[0] : '';
              const deepLinkUrl = `/time-machine?scanId=${tile.WebsitesDataID}${dateStr ? `&date=${dateStr}` : ''}`;
              return (
                <Box 
                  key={idx} 
                  component="a"
                  href={deepLinkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    width: '140px',
                    height: '100px',
                    backgroundColor: '#fff',
                    boxShadow: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform 0.2s',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: 2
                    }
                  }}
                >
                  <SightingCropImage 
                    WebsitesDataID={tile.WebsitesDataID}
                    BoundingBoxID={tile.BoundingBoxID}
                    BoundingBox={tile.BoundingBox}
                  />
                </Box>
              );
            })}
          </Box>
        ) : (
          <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
            No visual crops captured in database.
          </Typography>
        )}
      </Box>
    );
  };

  const renderCollapsibleContent = (gameA, gameB) => {
    const detailsA = gameDetails[gameA.GameID];
    const detailsB = gameDetails[gameB.GameID];
    const loadingA = detailsLoading[gameA.GameID];
    const loadingB = detailsLoading[gameB.GameID];

    if (loadingA || loadingB) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3, gap: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">Fetching details for merge comparison...</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ py: 2, px: 3, backgroundColor: 'action.hover' }}>
        <Grid container spacing={4}>
          <Grid item xs={6} sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: 'text.primary' }}>
              {gameA.Name} Sighting Stats &amp; Samples
            </Typography>
            {renderGameDetailCompare(gameA, detailsA)}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: 'text.primary' }}>
              {gameB.Name} Sighting Stats &amp; Samples
            </Typography>
            {renderGameDetailCompare(gameB, detailsB)}
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Box sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
        <SettingsIcon color="primary" fontSize="large" /> Game Management Workspace
        <IconButton 
          onClick={() => setWhatsNewOpen(true)} 
          size="small" 
          color="primary"
          sx={{ ml: 1, border: 1, borderColor: 'primary.light', p: 0.5 }}
          title="What's New Guide"
        >
          <HelpIcon fontSize="small" />
        </IconButton>
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Manage duplicate game names and cluster them into families. Ensure high-fidelity cross-provider data merging with safety limits.
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)}>
          <Tab label="Merge Suggestions" icon={<MergeIcon />} iconPosition="start" />
          <Tab label="Series & Families" icon={<FamilyIcon />} iconPosition="start" />
          <Tab label="Manual Merge" icon={<MergeIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: 'white', borderRadius: 2 }}>
              <CardContent sx={{ py: 3 }}>
                <Grid container spacing={4} alignItems="center">
                  <Grid item xs={12} md={7}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Similarity Threshold
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                      Configure the sensitivity of the SequenceMatcher duplication scan. Higher values isolate exact mismatches; lower values detect broader variations.
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Slider
                        value={similarityThreshold}
                        min={0.80}
                        max={1.00}
                        step={0.01}
                        valueLabelDisplay="auto"
                        onChange={(e, val) => setSimilarityThreshold(val)}
                        sx={{ color: 'white' }}
                      />
                      <Typography variant="h5" sx={{ fontWeight: 'bold', minWidth: '60px' }}>
                        {(similarityThreshold * 100).toFixed(0)}%
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: { md: 'flex-end', xs: 'flex-start' } }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="large"
                      onClick={fetchSuggestions}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                      disabled={loading}
                      sx={{ 
                        backgroundColor: '#ff4081', 
                        color: 'white', 
                        fontWeight: 'bold', 
                        px: 4, 
                        py: 1.5,
                        '&:hover': { backgroundColor: '#f50057' }
                      }}
                    >
                      Scan Database
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', flexDirection: 'column', gap: 2 }}>
                  <CircularProgress size={60} />
                  <Typography variant="body1" color="text.secondary">
                    Scanning Games table for duplicates...
                  </Typography>
                </Box>
              ) : suggestions.length === 0 ? (
                <Box sx={{ p: 6, textCenter: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <DoneIcon color="success" sx={{ fontSize: 60 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>All Clean!</Typography>
                  <Typography variant="body2" color="text.secondary">
                    No duplicate game suggestions found above the {(similarityThreshold * 100).toFixed(0)}% similarity threshold.
                  </Typography>
                </Box>
              ) : (
                <Table>
                  <TableHead sx={{ backgroundColor: 'action.hover' }}>
                    <TableRow>
                      <TableCell style={{ width: '50px' }} />
                      <TableCell sx={{ fontWeight: 'bold' }}>Similarity</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Game A (Candidate A)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Game B (Candidate B)</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Deduplication Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {suggestions.map((item, idx) => {
                      const rowKey = `${item.gameA.GameID}_${item.gameB.GameID}`;
                      const isExpanded = expandedRows.has(rowKey);
                      return (
                        <React.Fragment key={idx}>
                          <TableRow hover>
                            <TableCell sx={{ verticalAlign: 'middle' }}>
                              <IconButton
                                size="small"
                                onClick={() => toggleRowExpand(rowKey, item.gameA.GameID, item.gameB.GameID)}
                              >
                                {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'middle' }}>
                              {renderSimilarityChip(item.similarity)}
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography sx={{ fontWeight: 'bold' }}>{item.gameA.Name}</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                ID: {item.gameA.GameID}
                              </Typography>
                              <Typography variant="caption" color="warning.main" display="block" sx={{ fontWeight: 'bold' }}>
                                Studio: {studios.find(s => s.StudioID === item.gameA.StudioID)?.Name || 'Unknown'} ({item.gameA.StudioID})
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                First Seen: {item.gameA.Timestamp ? new Date(item.gameA.Timestamp).toLocaleString() : 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography sx={{ fontWeight: 'bold' }}>{item.gameB.Name}</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                ID: {item.gameB.GameID}
                              </Typography>
                              <Typography variant="caption" color="warning.main" display="block" sx={{ fontWeight: 'bold' }}>
                                Studio: {studios.find(s => s.StudioID === item.gameB.StudioID)?.Name || 'Unknown'} ({item.gameB.StudioID})
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                First Seen: {item.gameB.Timestamp ? new Date(item.gameB.Timestamp).toLocaleString() : 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    onClick={() => openConfirmation('merge', { source: item.gameA, target: item.gameB })}
                                    startIcon={<MergeIcon />}
                                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                                  >
                                    Merge A ➔ B
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    onClick={() => openConfirmation('merge', { source: item.gameB, target: item.gameA })}
                                    startIcon={<MergeIcon />}
                                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                                  >
                                    Merge B ➔ A
                                  </Button>
                                </Box>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => openConfirmation('ignore', { gameA: item.gameA, gameB: item.gameB })}
                                  startIcon={<IgnoreIcon />}
                                  sx={{ textTransform: 'none', width: '80%' }}
                                >
                                  Ignore Suggestion
                                </Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                {renderCollapsibleContent(item.gameA, item.gameB)}
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Box>
          {familiesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', flexDirection: 'column', gap: 2 }}>
              <CircularProgress size={50} />
              <Typography color="text.secondary">Loading Game Families workspace...</Typography>
            </Box>
          ) : (
            <Box>
              {/* Row of 4 Control Buttons */}
              <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<AddIcon />}
                  onClick={handleStartCreateNew}
                  sx={{ 
                    textTransform: 'none', 
                    fontWeight: 'bold', 
                    px: 3, 
                    py: 1, 
                    borderRadius: 2, 
                    boxShadow: 2,
                    backgroundColor: '#ed6c02', // Orange color scheme
                    '&:hover': {
                      backgroundColor: '#e65100'
                    }
                  }}
                >
                  New Family
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleOpenNearDuplicates}
                  sx={{ textTransform: 'none', fontWeight: 'bold', px: 3, py: 1, borderRadius: 2 }}
                >
                  Find Duplicates
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setSuggestNewFamiliesDialogOpen(true)}
                  sx={{ textTransform: 'none', fontWeight: 'bold', px: 3, py: 1, borderRadius: 2 }}
                >
                  Suggest New Families
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setSuggestAddFamiliesDialogOpen(true)}
                  sx={{ textTransform: 'none', fontWeight: 'bold', px: 3, py: 1, borderRadius: 2 }}
                >
                  Suggest Add to Families
                </Button>
              </Box>

              <Grid container spacing={3}>
                {/* Left Pane: Searchable Tree View */}
                <Grid item xs={12} md={4}>
                  <Card sx={{ borderRadius: 2, boxShadow: 2, p: 2 }}>
                    <Box sx={{ pb: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Game Families</Typography>
                      </Box>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search families or studios..."
                      value={treeSearch}
                      onChange={(e) => setTreeSearch(e.target.value)}
                      InputProps={{
                        startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      }}
                    />
                  </Box>
                  <Box sx={{ pt: 2 }}>
                    {getFilteredTreeData().length === 0 ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" align="center">No matching families found.</Typography>
                      </Box>
                    ) : (
                      <List disablePadding>
                        {getFilteredTreeData().map((studioGroup) => {
                          const isExpanded = expandedStudios.has(studioGroup.studioId);
                          return (
                            <Box key={studioGroup.studioId} sx={{ mb: 1 }}>
                              {/* Studio Header Row */}
                              <ListItem
                                button
                                onClick={() => toggleStudioExpand(studioGroup.studioId)}
                                sx={{
                                  borderRadius: 1,
                                  py: 1,
                                  backgroundColor: 'action.hover',
                                  '&:hover': { backgroundColor: 'action.selected' }
                                }}
                              >
                                <IconButton size="small" disableRipple sx={{ mr: 0.5 }}>
                                  {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                                </IconButton>
                                <FolderIcon sx={{ color: 'warning.main', mr: 1, fontSize: 20 }} />
                                <ListItemText 
                                  primary={studioGroup.studioName} 
                                  primaryTypographyProps={{ sx: { fontWeight: 'bold', fontSize: '0.8rem', letterSpacing: 0.5, color: 'text.secondary' } }} 
                                />
                                <Chip label={studioGroup.families.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                              </ListItem>

                              {/* Collapsible Families List */}
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <List disablePadding sx={{ pl: 4 }}>
                                  {studioGroup.families.map((family) => {
                                    const isSelected = selectedFamily?.FamilyID === family.FamilyID;
                                    return (
                                      <ListItem
                                        key={family.FamilyID}
                                        button
                                        onClick={() => handleSelectFamily(family)}
                                        sx={{
                                          borderRadius: 1,
                                          my: 0.5,
                                          backgroundColor: isSelected ? 'primary.light' : 'transparent',
                                          color: isSelected ? 'primary.contrastText' : 'inherit',
                                          '&:hover': { backgroundColor: isSelected ? 'primary.light' : 'action.hover' }
                                        }}
                                      >
                                        <FamilyIcon sx={{ mr: 1, fontSize: 18, opacity: 0.8 }} />
                                        <ListItemText 
                                          primary={family.DisplayName} 
                                          secondary={family.SeriesName ? `Series: ${family.SeriesName}` : null}
                                          primaryTypographyProps={{ sx: { fontSize: '0.9rem', fontWeight: 500 } }}
                                          secondaryTypographyProps={{ sx: { fontSize: '0.75rem', color: isSelected ? 'rgba(255,255,255,0.7)' : 'text.secondary' } }}
                                        />
                                      </ListItem>
                                    );
                                  })}
                                </List>
                              </Collapse>
                            </Box>
                          );
                        })}
                      </List>
                    )}
                  </Box>
                </Card>
              </Grid>

              {/* Right Pane: Family Editor */}
              <Grid item xs={12} md={8}>
                <Card sx={{ borderRadius: 2, boxShadow: 2, p: 3 }}>
                  {!selectedFamily && !isCreatingNew ? (
                    <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <FamilyIcon sx={{ fontSize: 80, color: 'text.disabled' }} />
                      <Typography variant="h6" color="text.secondary" align="center" sx={{ fontWeight: 'bold' }}>
                        No Family Selected
                      </Typography>
                      <Typography variant="body2" color="text.disabled" align="center">
                        Select a Game Family from the left pane to edit its properties, or click "New Family" to create a new one.
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {/* Editor Title & Controls */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider', pb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                          {isCreatingNew ? <AddIcon color="primary" /> : <EditIcon color="primary" />}
                          {isCreatingNew ? 'Create New Game Family' : `Manage Family: ${selectedFamily.DisplayName}`}
                        </Typography>
                        {!isCreatingNew && (
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={handleDeleteFamily}
                            sx={{ textTransform: 'none' }}
                          >
                            Delete Family
                          </Button>
                        )}
                      </Box>

                      {/* Main Properties Form */}
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Display Name"
                            placeholder="e.g. Book of Dead Series"
                            value={familyForm.DisplayName}
                            onChange={(e) => setFamilyForm({ ...familyForm, DisplayName: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Series Phrase Match"
                            placeholder="e.g. book of dead"
                            value={familyForm.SeriesName}
                            onChange={(e) => setFamilyForm({ ...familyForm, SeriesName: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Slug"
                            placeholder="e.g. book-of-dead"
                            value={familyForm.Slug}
                            onChange={(e) => setFamilyForm({ ...familyForm, Slug: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Associated Studio</InputLabel>
                            <Select
                              value={familyForm.StudioID}
                              label="Associated Studio"
                              onChange={(e) => setFamilyForm({ ...familyForm, StudioID: e.target.value })}
                            >
                              <MenuItem value="unknown">UNKNOWN STUDIO</MenuItem>
                              {studios.map(s => (
                                <MenuItem key={s.StudioID} value={s.StudioID}>{s.Name.toUpperCase()}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>

                      {/* Assigned Games Section */}
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}>
                          Assigned Games ({familyForm.GameIDs.length})
                        </Typography>
                        <Paper variant="outlined" sx={{ maxHeight: '300px', overflowY: 'auto', p: 1, backgroundColor: 'action.hover' }}>
                          {familyForm.GameIDs.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                              No games currently grouped under this family.
                            </Typography>
                          ) : (
                            <List dense disablePadding>
                              {familyForm.GameIDs.map(gameId => {
                                const gameName = gameMap[gameId] || `Unknown Game (${gameId})`;
                                return (
                                  <ListItem
                                    key={gameId}
                                    sx={{
                                      borderBottom: '1px solid',
                                      borderColor: 'divider',
                                      '&:last-child': { borderBottom: 'none' }
                                    }}
                                  >
                                    <ListItemText 
                                      primary={gameName} 
                                      secondary={`ID: ${gameId}`} 
                                      primaryTypographyProps={{ sx: { fontWeight: 'bold' } }}
                                    />
                                    <IconButton 
                                      edge="end" 
                                      color="error" 
                                      size="small" 
                                      onClick={() => handleRemoveGameFromFamily(gameId)}
                                    >
                                      <CloseIcon />
                                    </IconButton>
                                  </ListItem>
                                );
                              })}
                            </List>
                          )}
                        </Paper>
                      </Box>

                      {/* Add Game Pane */}
                      <Card variant="outlined" sx={{ p: 2, backgroundColor: 'background.paper' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SearchIcon fontSize="small" /> Add Games Manually
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Type game name to search..."
                            value={gameSearchQuery}
                            onChange={(e) => setGameSearchQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && searchGames()}
                          />
                          <Button
                            variant="contained"
                            onClick={searchGames}
                            disabled={searchingGames}
                            startIcon={searchingGames ? <CircularProgress size={16} /> : <SearchIcon />}
                            sx={{ textTransform: 'none' }}
                          >
                            Search
                          </Button>
                        </Box>

                        {gameSearchResults.length > 0 && (
                          <List dense sx={{ maxHeight: '200px', overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                            {gameSearchResults.map(game => {
                              const alreadyAdded = familyForm.GameIDs.includes(game.GameID);
                              return (
                                <ListItem
                                  key={game.GameID}
                                  sx={{
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    '&:last-child': { borderBottom: 'none' }
                                  }}
                                >
                                  <ListItemText 
                                    primary={game.Name} 
                                    secondary={`ID: ${game.GameID}`}
                                    primaryTypographyProps={{ sx: { fontWeight: 600 } }}
                                  />
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color={alreadyAdded ? "inherit" : "primary"}
                                    disabled={alreadyAdded}
                                    onClick={() => handleAddGameToFamily(game)}
                                    startIcon={alreadyAdded ? <DoneIcon /> : <AddIcon />}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    {alreadyAdded ? 'Added' : 'Add'}
                                  </Button>
                                </ListItem>
                              );
                            })}
                          </List>
                        )}
                      </Card>

                      {/* Footer Actions */}
                      <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2, display: 'flex', gap: 2 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={actionLoading}
                          onClick={handleSaveFamily}
                          sx={{ px: 4, fontWeight: 'bold' }}
                        >
                          {actionLoading ? <CircularProgress size={20} /> : 'Save Changes'}
                        </Button>
                        <Button
                          variant="outlined"
                          disabled={actionLoading}
                          onClick={() => {
                            setSelectedFamily(null);
                            setIsCreatingNew(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    )}

{activeTab === 2 && (
        <Box sx={{ mt: 3 }}>
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                Manual Game Merge
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 4 }}>
                Select a duplicate game to remove, and a target game to merge it into. 
                <strong style={{ color: '#d32f2f' }}> The duplicate game will be permanently deleted and its properties merged into the target.</strong>
              </Typography>
              
              <Grid container spacing={4}>
                <Grid item xs={12} md={5}>
                  <Box sx={{ border: '2px solid #ef5350', borderRadius: 2, p: 3, backgroundColor: '#ffebee' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#c62828', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DeleteIcon /> SOURCE GAME (Will be Zapped!)
                    </Typography>
                    <Autocomplete
                      options={allGames}
                      getOptionLabel={(option) => `${option.Name} (${option.GameID})`}
                      value={manualSourceGame}
                      onChange={(event, newValue) => setManualSourceGame(newValue)}
                      renderInput={(params) => <TextField {...params} label="Search Duplicate Game" variant="outlined" sx={{ backgroundColor: 'white' }} />}
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <MergeIcon sx={{ fontSize: 60, color: 'text.disabled' }} />
                </Grid>

                <Grid item xs={12} md={5}>
                  <Box sx={{ border: '2px solid #4caf50', borderRadius: 2, p: 3, backgroundColor: '#e8f5e9' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2e7d32', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DoneIcon /> TARGET GAME (Will be Kept!)
                    </Typography>
                    <Autocomplete
                      options={allGames}
                      getOptionLabel={(option) => `${option.Name} (${option.GameID})`}
                      value={manualTargetGame}
                      onChange={(event, newValue) => setManualTargetGame(newValue)}
                      renderInput={(params) => <TextField {...params} label="Search Master Game" variant="outlined" sx={{ backgroundColor: 'white' }} />}
                    />
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="warning"
                  size="large"
                  disabled={!manualSourceGame || !manualTargetGame || manualSourceGame.GameID === manualTargetGame.GameID || actionLoading}
                  startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <MergeIcon />}
                  onClick={() => {
                    if (window.confirm(`Are you absolutely sure you want to merge "${manualSourceGame.Name}" INTO "${manualTargetGame.Name}"?\n\n"${manualSourceGame.Name}" will be permanently deleted.`)) {
                      handleMergeAction(manualSourceGame.GameID, manualTargetGame.GameID).then(() => {
                        setManualSourceGame(null);
                        setManualTargetGame(null);
                      });
                    }
                  }}
                  sx={{ py: 1.5, px: 4, fontWeight: 'bold', fontSize: '1.1rem' }}
                >
                  EXECUTE MERGE
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => !actionLoading && setConfirmDialog({ open: false, type: '', data: null })}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {confirmDialog.type === 'merge' ? 'Confirm Game Merge' : 'Ignore Suggestion Pair'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {confirmDialog.type === 'merge' && confirmDialog.data && (
              <>
                You are about to merge:
                <Typography component="div" sx={{ fontWeight: 'bold', my: 1, color: 'error.main' }}>
                  ❌ {confirmDialog.data.source.Name} (ID: {confirmDialog.data.source.GameID})
                </Typography>
                into:
                <Typography component="div" sx={{ fontWeight: 'bold', my: 1, color: 'success.main' }}>
                  ✅ {confirmDialog.data.target.Name} (ID: {confirmDialog.data.target.GameID})
                </Typography>
                This action is **irreversible** and will:
                <ul>
                  <li>Re-link all past Unconfirmed Analysis items.</li>
                  <li>Re-link LLM matching configurations.</li>
                  <li>Update active game family listings (merging references automatically).</li>
                  <li>Retain the oldest launch timestamp (preserving First-Seen statistics).</li>
                  <li>Delete the source game record.</li>
                </ul>
              </>
            )}
            {confirmDialog.type === 'ignore' && confirmDialog.data && (
              <>
                Are you sure you want to ignore similarity matches between:
                <Typography component="div" sx={{ fontWeight: 'bold', my: 1 }}>
                  • {confirmDialog.data.gameA.Name}
                </Typography>
                and:
                <Typography component="div" sx={{ fontWeight: 'bold', my: 1 }}>
                  • {confirmDialog.data.gameB.Name}
                </Typography>
                You can restore suggestions by modifying the Ignore configuration table.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmDialog({ open: false, type: '', data: null })} disabled={actionLoading}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color={confirmDialog.type === 'merge' ? "warning" : "error"}
            disabled={actionLoading}
            onClick={() => {
              if (confirmDialog.type === 'merge') {
                handleMergeAction(confirmDialog.data.source.GameID, confirmDialog.data.target.GameID);
              } else {
                handleIgnoreAction(confirmDialog.data.gameA.GameID, confirmDialog.data.gameB.GameID);
              }
            }}
          >
            {actionLoading ? <CircularProgress size={20} color="inherit" /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Near Duplicates Dialog */}
      <Dialog 
        open={nearDuplicatesDialogOpen} 
        onClose={() => setNearDuplicatesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', borderBottom: 1, borderColor: 'divider', pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>🔍 Near-Duplicate Families Scan</span>
          <IconButton onClick={() => setNearDuplicatesDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 0 }}>
          <DialogContentText sx={{ mb: 2 }}>
            The following game family clusters have been detected as potential near-duplicates due to overlapping GameIDs or highly similar display/series names within the same studio.
          </DialogContentText>
          {nearDuplicates.length === 0 ? (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              No near-duplicate families found! Everything looks perfectly segmented.
            </Alert>
          ) : (() => {
            const matches = nearDuplicates;
            const match = matches[activeMatchIdx] || null;
            if (!match) return null;
            
            const studio = studios.find(s => s.StudioID === match.familyA.StudioID);
            const studioName = studio ? studio.Name.toUpperCase() : 'UNKNOWN STUDIO';
            
            const gamesA = match.familyA.GameIDs || [];
            const gamesB = match.familyB.GameIDs || [];
            const setA = new Set(gamesA);
            const setB = new Set(gamesB);
            
            // Generate comparison rows
            const commonGames = gamesA.filter(id => setB.has(id));
            const onlyA = gamesA.filter(id => !setB.has(id));
            const onlyB = gamesB.filter(id => !setA.has(id));
            
            const gameRows = [];
            commonGames.forEach(id => {
              gameRows.push({ id, type: 'common', nameA: getGameName(id), nameB: getGameName(id) });
            });
            const maxDiff = Math.max(onlyA.length, onlyB.length);
            for (let i = 0; i < maxDiff; i++) {
              const idA = onlyA[i] || null;
              const idB = onlyB[i] || null;
              gameRows.push({
                type: 'diff',
                idA,
                idB,
                nameA: idA ? getGameName(idA) : '',
                nameB: idB ? getGameName(idB) : ''
              });
            }

            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                
                {/* 1. Previous / Next Controls at the Top */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  p: 1.5, 
                  bgcolor: 'action.hover', 
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider'
                }}>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={() => handlePrevMatch(matches)}
                    disabled={activeMatchIdx === 0}
                    startIcon={<ChevronLeftIcon />}
                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                  >
                    Previous
                  </Button>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                      Match {activeMatchIdx + 1} of {matches.length}
                      <Chip 
                        label={`${Math.round(match.score * 100)}% Similarity`} 
                        color={match.overlapGames?.length > 0 ? "warning" : "info"}
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {match.reason}
                    </Typography>
                  </Box>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={() => handleNextMatch(matches)}
                    disabled={activeMatchIdx === matches.length - 1}
                    endIcon={<ChevronRightIcon />}
                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                  >
                    Next
                  </Button>
                </Box>

                {/* 2. Side-by-Side Family Comparison Cards */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Card 
                      variant="outlined" 
                      onClick={() => setSelectedTargetFamilyId(match.familyA.FamilyID)}
                      sx={{ 
                        p: 2, 
                        cursor: 'pointer',
                        borderRadius: 2,
                        position: 'relative',
                        borderColor: selectedTargetFamilyId === match.familyA.FamilyID ? 'warning.main' : 'divider',
                        backgroundColor: selectedTargetFamilyId === match.familyA.FamilyID ? 'rgba(237, 108, 2, 0.04)' : 'background.paper',
                        '&:hover': { borderColor: 'warning.light' },
                        transition: 'all 0.2s'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <input 
                          type="radio" 
                          checked={selectedTargetFamilyId === match.familyA.FamilyID}
                          onChange={() => {}} 
                          style={{ accentColor: '#ed6c02', width: 18, height: 18 }}
                        />
                        <IconButton 
                          color="error" 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFamilyFromDuplicates(match.familyA);
                          }}
                          disabled={actionLoading}
                          sx={{ p: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: selectedTargetFamilyId === match.familyA.FamilyID ? 'warning.dark' : 'text.primary' }}>
                            Keep: {match.familyA.DisplayName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Slug: {match.familyA.Slug} | Studio: {studioName}
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            Family A ({gamesA.length} games)
                          </Typography>
                        </Box>
                      </Box>
                      {selectedTargetFamilyId === match.familyA.FamilyID && (
                        <Chip 
                          label="CANONICAL KEEP" 
                          color="warning" 
                          size="small" 
                          sx={{ position: 'absolute', top: 12, right: 12, fontWeight: 'bold', fontSize: '0.65rem' }} 
                        />
                      )}
                    </Card>
                  </Grid>

                  <Grid item xs={6}>
                    <Card 
                      variant="outlined" 
                      onClick={() => setSelectedTargetFamilyId(match.familyB.FamilyID)}
                      sx={{ 
                        p: 2, 
                        cursor: 'pointer',
                        borderRadius: 2,
                        position: 'relative',
                        borderColor: selectedTargetFamilyId === match.familyB.FamilyID ? 'warning.main' : 'divider',
                        backgroundColor: selectedTargetFamilyId === match.familyB.FamilyID ? 'rgba(237, 108, 2, 0.04)' : 'background.paper',
                        '&:hover': { borderColor: 'warning.light' },
                        transition: 'all 0.2s'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <input 
                          type="radio" 
                          checked={selectedTargetFamilyId === match.familyB.FamilyID}
                          onChange={() => {}} 
                          style={{ accentColor: '#ed6c02', width: 18, height: 18 }}
                        />
                        <IconButton 
                          color="error" 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFamilyFromDuplicates(match.familyB);
                          }}
                          disabled={actionLoading}
                          sx={{ p: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: selectedTargetFamilyId === match.familyB.FamilyID ? 'warning.dark' : 'text.primary' }}>
                            Keep: {match.familyB.DisplayName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Slug: {match.familyB.Slug} | Studio: {studioName}
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            Family B ({gamesB.length} games)
                          </Typography>
                        </Box>
                      </Box>
                      {selectedTargetFamilyId === match.familyB.FamilyID && (
                        <Chip 
                          label="CANONICAL KEEP" 
                          color="warning" 
                          size="small" 
                          sx={{ position: 'absolute', top: 12, right: 12, fontWeight: 'bold', fontSize: '0.65rem' }} 
                        />
                      )}
                    </Card>
                  </Grid>
                </Grid>

                {/* 3. Color-Coded Side-by-Side Games List */}
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1 }}>
                  🎮 Games Comparison (Green = Common, Red = Difference)
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: '350px', overflowY: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Games in Family A ({match.familyA.DisplayName})</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Games in Family B ({match.familyB.DisplayName})</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {gameRows.map((row, rIdx) => {
                        if (row.type === 'common') {
                          return (
                            <TableRow key={rIdx} sx={{ bgcolor: 'rgba(76, 175, 80, 0.08)', '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.15)' } }}>
                              <TableCell sx={{ color: 'success.dark', fontWeight: 'medium', py: 0.75 }}>
                                {row.nameA}
                              </TableCell>
                              <TableCell sx={{ color: 'success.dark', fontWeight: 'medium', py: 0.75 }}>
                                {row.nameB}
                              </TableCell>
                            </TableRow>
                          );
                        } else {
                          return (
                            <TableRow key={rIdx}>
                              <TableCell sx={{ 
                                bgcolor: row.idA ? 'rgba(244, 67, 54, 0.08)' : 'transparent',
                                '&:hover': { bgcolor: row.idA ? 'rgba(244, 67, 54, 0.15)' : 'transparent' },
                                color: 'error.dark', 
                                py: 0.75
                              }}>
                                {row.nameA || <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>— Not in Family A —</Typography>}
                              </TableCell>
                              <TableCell sx={{ 
                                bgcolor: row.idB ? 'rgba(244, 67, 54, 0.08)' : 'transparent',
                                '&:hover': { bgcolor: row.idB ? 'rgba(244, 67, 54, 0.15)' : 'transparent' },
                                color: 'error.dark', 
                                py: 0.75
                              }}>
                                {row.nameB || <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>— Not in Family B —</Typography>}
                              </TableCell>
                            </TableRow>
                          );
                        }
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Alert severity="warning" sx={{ mt: 1, borderRadius: 2 }}>
                  Merging will combine the games of both families into the selected canonical family, and permanently delete the other family. This cannot be undone.
                </Alert>

              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider', mt: 2 }}>
          <Button onClick={() => setNearDuplicatesDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 'bold' }}>
            Cancel
          </Button>
          {nearDuplicates.length > 0 && (() => {
            const matches = nearDuplicates;
            const match = matches[activeMatchIdx];
            return (
              <Button 
                variant="contained" 
                color="warning"
                disabled={actionLoading}
                onClick={async () => {
                  setActionLoading(true);
                  try {
                    const targetFamily = match.familyA.FamilyID === selectedTargetFamilyId ? match.familyA : match.familyB;
                    const sourceFamily = match.familyA.FamilyID === selectedTargetFamilyId ? match.familyB : match.familyA;
                    
                    const unionGameIDs = Array.from(new Set([
                      ...(targetFamily.GameIDs || []),
                      ...(sourceFamily.GameIDs || [])
                    ]));
                    
                    const updateRes = await axios.put(`${API_URL}/game-management/families/${targetFamily.FamilyID}`, {
                      ...targetFamily,
                      GameIDs: unionGameIDs
                    }, {
                      headers: { 'x-api-key': API_KEY }
                    });
                    
                    await axios.delete(`${API_URL}/game-management/families/${sourceFamily.FamilyID}`, {
                      headers: { 'x-api-key': API_KEY }
                    });
                    
                    showNotification(`Successfully merged family "${sourceFamily.DisplayName}" into "${targetFamily.DisplayName}"!`, 'success');
                    
                    setFamilies(prev => prev
                      .filter(f => f.FamilyID !== sourceFamily.FamilyID)
                      .map(f => f.FamilyID === targetFamily.FamilyID ? updateRes.data.family : f)
                    );
                    
                    // Adjust active index if we merged the last pair
                    const remainingCount = matches.length - 1;
                    if (remainingCount === 0) {
                      setNearDuplicatesDialogOpen(false);
                    } else {
                      const nextIdx = Math.min(activeMatchIdx, remainingCount - 1);
                      setActiveMatchIdx(nextIdx);
                      // Update active target selection for next pair
                      const nextMatches = matches.filter((_, idx) => idx !== activeMatchIdx);
                      setSelectedTargetFamilyId(nextMatches[nextIdx].familyA.FamilyID);
                    }
                  } catch (err) {
                    console.error(err);
                    showNotification('Failed to merge families.', 'error');
                  } finally {
                    setActionLoading(false);
                  }
                }}
                sx={{ 
                  textTransform: 'none', 
                  fontWeight: 'bold',
                  backgroundColor: '#ed6c02',
                  '&:hover': { backgroundColor: '#e65100' }
                }}
              >
                {actionLoading ? <CircularProgress size={20} color="inherit" /> : 'Merge Families'}
              </Button>
            );
          })()}
        </DialogActions>
      </Dialog>

      {/* Suggest New Families Dialog */}
      <Dialog 
        open={suggestNewFamiliesDialogOpen} 
        onClose={() => setSuggestNewFamiliesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          💡 Suggested New Game Families
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <DialogContentText sx={{ mb: 2 }}>
            The system scanned the database using advanced token n-gram analysis to discover recurring game name clusters per studio. The following families do not yet exist in your records.
          </DialogContentText>
          {suggestedNewFamilies.length === 0 ? (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              Everything is clean! No new repeating game series were detected.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead sx={{ backgroundColor: 'action.hover' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Suggested Family</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Studio</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Detected Game Matches</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const familyGameSets = families.map(f => ({
                      family: f,
                      gameIDsSet: new Set(f.GameIDs || [])
                    }));
                    return suggestedNewFamilies.map((suggested, idx) => {
                      const studioId = suggested.StudioID;
                      const phraseLower = suggested.SeriesName.toLowerCase();
                      
                      // Find existing families for this studio containing the phrase
                      const relatedFamilies = families.filter(f => 
                        f.StudioID === studioId && 
                        (
                          (f.DisplayName || '').toLowerCase().includes(phraseLower) || 
                          (f.SeriesName || '').toLowerCase().includes(phraseLower) ||
                          (f.Slug || '').toLowerCase().includes(phraseLower)
                        )
                      );

                      const crossoverFamilies = [];
                      familyGameSets.forEach(({ family, gameIDsSet }) => {
                        const common = suggested.GameIDs.filter(id => gameIDsSet.has(id));
                        if (common.length > 0) {
                          const pct = Math.round((common.length / suggested.GameIDs.length) * 100);
                          crossoverFamilies.push({
                            family,
                            commonGameIDs: common,
                            pct
                          });
                        }
                      });
                      crossoverFamilies.sort((a, b) => b.pct - a.pct);

                      return (
                        <TableRow key={idx} hover>
                          <TableCell sx={{ verticalAlign: 'middle' }}>
                            <TextField
                              size="small"
                              variant="outlined"
                              label="Family Name"
                              value={customNames[`${suggested.StudioID}||${suggested.SeriesName}`] !== undefined 
                                ? customNames[`${suggested.StudioID}||${suggested.SeriesName}`] 
                                : suggested.SeriesName.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                              onChange={(e) => setCustomNames(prev => ({
                                ...prev,
                                [`${suggested.StudioID}||${suggested.SeriesName}`]: e.target.value
                              }))}
                              sx={{ mb: 1, mt: 1, width: '100%', minWidth: '200px' }}
                            />
                            <Typography variant="caption" color="text.secondary" display="block">
                              Core phrase: "{suggested.SeriesName}"
                            </Typography>
                            
                            {relatedFamilies.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'error.main', display: 'block', mb: 0.5 }}>
                                  ⚠️ Existing Studio Families with "{suggested.SeriesName}":
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {relatedFamilies.map(rf => (
                                    <Chip 
                                      key={rf.FamilyID}
                                      label={rf.DisplayName || rf.FamilyID}
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem', height: 20 }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}

                            {crossoverFamilies.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'warning.main', display: 'block', mb: 0.5 }}>
                                  ⚠️ Game Crossover with Existing Families:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {crossoverFamilies.map(cf => {
                                    const tooltipContent = (
                                      <Box sx={{ p: 0.5 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                          Games in "{cf.family.DisplayName}":
                                        </Typography>
                                        {cf.family.GameIDs.map(id => (
                                          <Typography key={id} variant="caption" display="block">
                                            • {gameMap[id] || `Unknown (${id})`}
                                          </Typography>
                                        ))}
                                      </Box>
                                    );
                                    return (
                                      <Tooltip key={cf.family.FamilyID} title={tooltipContent} arrow>
                                        <Chip 
                                          label={`${cf.family.DisplayName || cf.family.FamilyID} (${cf.pct}% overlap)`}
                                          size="small"
                                          color="warning"
                                          variant="outlined"
                                          sx={{ fontSize: '0.7rem', height: 20, cursor: 'help' }}
                                        />
                                      </Tooltip>
                                    );
                                  })}
                                </Box>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>
                            <Typography sx={{ fontWeight: 'bold' }}>
                              {suggested.StudioName.toUpperCase()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {suggested.StudioID}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>
                            <Typography sx={{ fontWeight: 'bold', mb: 0.5 }} color="primary">
                              {suggested.GameCount} games found:
                            </Typography>
                            <Box sx={{ pl: 1, borderLeft: 2, borderColor: 'divider' }}>
                              {suggested.GameIDs.map(id => (
                                <Typography key={id} variant="caption" display="block" color="text.secondary">
                                  • {gameMap[id] || `Unknown (${id})`}
                                </Typography>
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={() => handleAcceptSuggestedNewFamily(suggested, customNames[`${suggested.StudioID}||${suggested.SeriesName}`])}
                                sx={{ textTransform: 'none', fontWeight: 'bold', width: '100%' }}
                              >
                                Create Family
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                disabled={actionLoading}
                                startIcon={<IgnoreIcon />}
                                onClick={() => handleIgnoreSeriesAction(suggested.SeriesName)}
                                sx={{ textTransform: 'none', fontWeight: 'bold', width: '100%' }}
                              >
                                Ignore
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSuggestNewFamiliesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Suggest Add to Families Dialog */}
      <Dialog 
        open={suggestAddFamiliesDialogOpen} 
        onClose={() => setSuggestAddFamiliesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          ➕ Suggest Additions to Existing Families
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <DialogContentText sx={{ mb: 2 }}>
            The system detected active games belonging to a family's studio that contain the family's series phrase in their name, but are not yet assigned to the family.
          </DialogContentText>
          {suggestedAddFamilies.length === 0 ? (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              Excellent! All matching games are correctly cataloged into their respective families.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead sx={{ backgroundColor: 'action.hover' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Existing Family</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Studio</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>New Matching Games</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suggestedAddFamilies.map((item, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        <Typography sx={{ fontWeight: 'bold' }}>
                          {item.family.DisplayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Series term: "{item.family.SeriesName}"
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        <Typography sx={{ fontWeight: 'bold' }}>
                          {item.studioName.toUpperCase()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {item.family.StudioID}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        <Typography sx={{ fontWeight: 'bold', mb: 0.5 }} color="warning.main">
                          {item.newGames.length} new game(s) to add:
                        </Typography>
                        <Box sx={{ pl: 1, borderLeft: 2, borderColor: 'divider' }}>
                          {item.newGames.map(game => (
                            <Typography key={game.GameID} variant="caption" display="block" color="text.secondary">
                              • {game.Name}
                            </Typography>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          disabled={actionLoading}
                          onClick={() => handleAddGamesToFamily(item.family, item.newGames)}
                          sx={{ textTransform: 'none', fontWeight: 'bold' }}
                        >
                          {actionLoading ? <CircularProgress size={16} color="inherit" /> : 'Add Games'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSuggestAddFamiliesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* 🧭 What's New Guide Dialog */}
      <Dialog 
        open={whatsNewOpen} 
        onClose={handleCloseWhatsNew}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 24,
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>✨</span> {whatsNewLang === 'en' ? "What's New in Game Management!" : "Novidades no Gerenciamento de Jogos!"}
          </Typography>
          <ToggleButtonGroup
            value={whatsNewLang}
            exclusive
            onChange={(e, val) => val && setWhatsNewLang(val)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                py: 0.2,
                px: 1.5,
                fontSize: '1.2rem',
                borderRadius: '8px',
              }
            }}
          >
            <ToggleButton value="en" title="English">🇬🇧</ToggleButton>
            <ToggleButton value="pt" title="Português (Brasil)">🇧🇷</ToggleButton>
          </ToggleButtonGroup>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            {whatsNewLang === 'en' 
              ? "We have added several core enhancements to streamline how you audit, edit, and cluster game duplicate families."
              : "Adicionamos várias melhorias essenciais para simplificar como você audita, edita e agrupa famílias de jogos duplicados."
            }
          </Typography>
          
          <Stack spacing={2.5}>
            {(whatsNewLang === 'en' ? [
              {
                icon: "🔁",
                color: "rgba(25, 118, 210, 0.1)",
                title: "Continuous Batch Actions",
                text: "The 'Add to Family' dialog now stays open after actions. Add multiple game suggestions sequentially without repetitive dialog closing."
              },
              {
                icon: "✍️",
                color: "rgba(76, 175, 80, 0.1)",
                title: "Editable Suggestions",
                text: "You can now edit suggested family names directly within the suggestions table before saving, ensuring correct names from the start."
              },
              {
                icon: "⚠️",
                color: "rgba(255, 152, 0, 0.1)",
                title: "Crossover Warnings & Tooltips",
                text: "See overlap percentages directly on suggestions. Hovering over the crossover chip displays all existing family members by name."
              },
              {
                icon: "🗑️",
                color: "rgba(244, 67, 54, 0.1)",
                title: "Inline Family Deletion",
                text: "Click the red trash icon in the 'Find Duplicates' comparison cards to permanently delete non-mergeable redundant groups directly."
              },
              {
                icon: "🧭",
                color: "rgba(156, 39, 176, 0.1)",
                title: "Time Machine Clickthrough",
                text: "Clicking any Recent Game Crops tile now opens that screenshot and highlighted crop directly in the Time Machine workspace in a new browser tab."
              }
            ] : [
              {
                icon: "🔁",
                color: "rgba(25, 118, 210, 0.1)",
                title: "Ações contínuas em lote",
                text: "O diálogo de adição agora permanece aberto. Adicione sugestões de jogos sequencialmente sem fechamentos repetitivos."
              },
              {
                icon: "✍️",
                color: "rgba(76, 175, 80, 0.1)",
                title: "Sugestões editáveis",
                text: "Agora você pode editar os nomes sugeridos diretamente na tabela antes de salvar, garantindo nomes corretos desde o início."
              },
              {
                icon: "⚠️",
                color: "rgba(255, 152, 0, 0.1)",
                title: "Alertas de cruzamento e dicas (Tooltips)",
                text: "Visualize porcentagens de sobreposição. Passar o mouse sobre o aviso exibe todos os membros da família existente por nome."
              },
              {
                icon: "🗑️",
                color: "rgba(244, 67, 54, 0.1)",
                title: "Exclusão integrada de famílias",
                text: "Clique na lixeira vermelha nos cards de 'Buscar Duplicados' para excluir diretamente os grupos redundantes que não devem ser mesclados."
              },
              {
                icon: "🧭",
                color: "rgba(156, 39, 176, 0.1)",
                title: "Navegação para o Time Machine",
                text: "Ao clicar em qualquer imagem de recorte de jogo recente, o sistema abre essa captura de tela e recorte correspondente no Time Machine em uma nova aba do navegador."
              }
            ]).map((step, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{ fontSize: '1.75rem', bgcolor: step.color, p: 1, borderRadius: 2, lineHeight: 1 }}>
                  {step.icon}
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {step.text}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button 
            onClick={handleDontShowWhatsNew} 
            sx={{ textTransform: 'none', color: '#666' }}
          >
            {whatsNewLang === 'en' ? "Don't Show Again" : "Não mostrar novamente"}
          </Button>
          <Button 
            variant="contained" 
            color="warning"
            onClick={handleCloseWhatsNew}
            sx={{ px: 3, borderRadius: 2, color: '#fff' }}
          >
            {whatsNewLang === 'en' ? "Got it!" : "Entendi!"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%', borderRadius: 2 }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GameManagement;
